import * as React from "react";
import { Button, Input, Label } from "../components/Modal";
import { Capability, Snapshot } from "./OrganizationPermissionStore";

interface Props {
    snapshot: Snapshot;
    definitions: Capability[];
    busy: boolean;
    onCopy: (name: string, transferAssignments: boolean) => Promise<void>;
}

export default function CopyPermissionsToRole({ snapshot, definitions, busy, onCopy }: Props) {
    const [expanded, setExpanded] = React.useState(false);
    const [name, setName] = React.useState("");
    const [transfer, setTransfer] = React.useState(false);
    const direct = snapshot.permissions.filter(p => p.assignment !== "inherit");
    if (!snapshot.canCopyToRole) return null;
    const ready = direct.length > 0 && direct.every(p => p.editable && p.availability === "available");
    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (busy || !ready || !name.trim()) return;
        try { await onCopy(name.trim(), transfer); setExpanded(false); setName(""); setTransfer(false); }
        catch { /* The permissions screen displays the error and offers a fresh read. */ }
    };
    return <div className="rounded-lg border border-gray-200 p-4">
        {!expanded ? <>
            <Button type="button" variant="secondary" disabled={!ready || busy} onClick={() => setExpanded(true)}>Copy permissions to a new role</Button>
            {!ready && <p className="mt-2 text-sm text-gray-500">The user needs direct Identity permissions that you can manage.</p>}
        </> : <form onSubmit={submit} className="space-y-3">
            <h3 className="font-semibold text-gray-900">Copy permissions to a new role</h3>
            <p className="text-sm text-gray-600">Copy {direct.filter(p => p.assignment === "allow").length} direct grants and {direct.filter(p => p.assignment === "deny").length} direct denials. Inherited permissions and other permission scopes stay as they are.</p>
            <details className="text-sm text-gray-600"><summary className="cursor-pointer">View permissions to copy</summary>
                <ul className="mt-2 max-h-48 list-disc overflow-y-auto pl-5">{direct.map(p => <li key={p.permissionId}>{definitions.find(d => d.permissionId === p.permissionId)?.displayName || p.permissionId}: {p.assignment === "allow" ? "Allow" : "Deny"}</li>)}</ul>
            </details>
            <div><Label htmlFor="copy-role-name" required>New role name</Label><Input id="copy-role-name" value={name} onChange={event => setName(event.target.value)} maxLength={256} required /></div>
            <label className="flex items-start gap-2 text-sm text-gray-700"><input type="checkbox" checked={transfer} onChange={event => setTransfer(event.target.checked)} className="mt-1" />
                <span>Assign the new role to this user and remove the copied direct assignments.<span className="block text-gray-500">The user keeps these permissions through the role, including denials.</span></span>
            </label>
            <div className="flex gap-2"><Button type="submit" disabled={busy || !ready || !name.trim()}>{busy ? "Creating role…" : transfer ? "Create role and transfer permissions" : "Create role"}</Button>
                <Button type="button" variant="secondary" disabled={busy} onClick={() => setExpanded(false)}>Cancel</Button></div>
        </form>}
    </div>;
}
